"use client";

import { useRef } from "react";
import {
  CLASS_FILL,
  TILE_TEXT,
} from "@/components/tools/chem-reference/periodic-style";
import { t } from "@/lib/i18n/interpolate";
import {
  type ColorClass,
  type ColorMode,
  classOf,
  moveFocus,
  type NavKey,
  PERIODIC_ELEMENTS,
} from "@/lib/tool-kit/chem-reference/periodic";

const NAV_KEYS: ReadonlySet<string> = new Set([
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
]);

/** Row 8 is the visual gap between the main table and the f-block rows. */
const GRID_STYLE = {
  gridTemplateColumns: "repeat(18, minmax(2.6rem, 1fr))",
  gridTemplateRows: "repeat(7, auto) 0.5rem repeat(2, auto)",
} as const;

export interface GridStrings {
  gridLabel: string;
  cellLabel: string;
  classLabels: Record<string, string>;
}

/**
 * The 18-column table as a roving-tabindex group of buttons: Tab enters the grid once,
 * arrow keys move between tiles, Enter or Space selects.
 */
export function PeriodicGrid({
  mode,
  classes,
  selected,
  matches,
  filtering,
  strings,
  onSelect,
}: {
  mode: ColorMode;
  classes: readonly ColorClass[];
  selected: number;
  matches: ReadonlySet<number>;
  filtering: boolean;
  strings: GridStrings;
  onSelect: (z: number) => void;
}) {
  const container = useRef<HTMLDivElement>(null);
  const codes = new Map(classes.map((cls) => [cls.id, cls.code]));

  function handleKeyDown(event: React.KeyboardEvent, z: number) {
    if (!NAV_KEYS.has(event.key)) {
      return;
    }
    event.preventDefault();
    const next = moveFocus(z, event.key as NavKey);
    container.current
      ?.querySelector<HTMLButtonElement>(`[data-z="${next}"]`)
      ?.focus();
  }

  return (
    // biome-ignore lint/a11y/useSemanticElements: a 2-D roving-focus group, not a fieldset
    <div
      aria-label={strings.gridLabel}
      className="grid gap-1"
      ref={container}
      role="group"
      style={GRID_STYLE}
    >
      {PERIODIC_ELEMENTS.map((element) => {
        const classId = classOf(element, mode);
        const matched = matches.has(element.z);
        const isSelected = element.z === selected;
        const marker =
          filtering && matched ? "outline outline-2 outline-foreground" : "";
        const dim = filtering && !matched ? "opacity-25" : "";
        const ring = isSelected
          ? "ring-2 ring-primary ring-offset-1 ring-offset-background"
          : "";
        return (
          <button
            aria-label={t(strings.cellLabel, {
              name: element.name,
              symbol: element.symbol,
              z: String(element.z),
              class: strings.classLabels[classId] ?? classId,
            })}
            aria-pressed={isSelected}
            className={`relative flex flex-col items-center justify-center rounded-md border border-black/15 px-0.5 py-1 leading-none ${CLASS_FILL[classId]} ${TILE_TEXT} ${marker} ${dim} ${ring} focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2`}
            data-z={element.z}
            key={element.z}
            onClick={() => onSelect(element.z)}
            onKeyDown={(event) => handleKeyDown(event, element.z)}
            style={{ gridColumn: element.col, gridRow: element.row }}
            tabIndex={isSelected ? 0 : -1}
            type="button"
          >
            <span className="self-start pl-0.5 text-[10px]">{element.z}</span>
            <span className="font-semibold text-base">{element.symbol}</span>
            <span className="text-[10px]">{codes.get(classId)}</span>
          </button>
        );
      })}
    </div>
  );
}
